import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "./InputField";

/**
 * Map a stored gender value onto the option set this select offers.
 *
 * Profiles were written by several different forms over time, so the same
 * person may be stored as "male", "Male", "L", or "Laki-laki". The select only
 * matches on exact option values, so anything unrecognised silently fell back
 * to the "Pilih Gender" placeholder and looked like the profile had never been
 * filled in — even though the value was there all along.
 *
 * Returns "" only when the value is genuinely absent or unrecognised, so a
 * blank really does mean blank.
 */
const normalizeGender = (raw) => {
  const value = String(raw ?? "").trim().toLowerCase();
  if (!value) return "";
  if (["male", "m", "l", "laki-laki", "laki laki", "pria"].includes(value)) return "Laki-laki";
  if (["female", "f", "p", "perempuan", "wanita"].includes(value)) return "Perempuan";
  return "";
};

export const InputFieldProfile = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
  showIdentityNumbers = true,
}) => {
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    countries: false,
    provinces: false,
    cities: false,
  });

  const geoNamesUsername = "xtrifeinnie";

  const cache = useMemo(() => new Map(), []);

  const fetchWithCache = async (url, cacheKey, type) => {
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.geonames) {
        cache.set(cacheKey, data);
        return data;
      } else {
        console.error(`No geonames data in response for ${cacheKey}:`, data);
        setErrors((prev) => ({
          ...prev,
          [type]: "Gagal memuat data lokasi. Silakan coba lagi nanti.",
        }));
        return null;
      }
    } catch (err) {
      console.error(`Error fetching ${cacheKey}:`, err);
      setErrors((prev) => ({
        ...prev,
        [type]: "Gagal memuat data lokasi. Silakan coba lagi nanti.",
      }));
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    fetchWithCache(
      `https://secure.geonames.org/countryInfoJSON?username=${geoNamesUsername}`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const countryList = data.geonames.map((country) => ({
          value: country.countryName,
          label: country.countryName,
        }));
        setCountries(countryList);
        setRawCountries(data.geonames);
      } else {
        console.warn("Using empty country list as fallback");
        setCountries([]);
        setRawCountries([]);
      }
    });
  }, []);

  useEffect(() => {
    if (formData.negara && rawCountries.length > 0) {
      setProvinces([]);
      setCities([]);
      const selectedCountry = rawCountries.find(
        (c) => c.countryName === formData.negara
      );
      if (!selectedCountry) {
        console.warn(`Country not found: ${formData.negara}`);
        return;
      }

      fetchWithCache(
        `https://secure.geonames.org/childrenJSON?geonameId=${selectedCountry.geonameId}&username=${geoNamesUsername}&lang=id`,
        `provinces_${selectedCountry.geonameId}`,
        "provinces"
      ).then((data) => {
        if (data?.geonames) {
          const provinceList = data.geonames.map((prov) => ({
            value: prov.name,
            label: prov.name,
          }));
          const idMap = {};
          data.geonames.forEach((prov) => {
            idMap[prov.name] = prov.geonameId;
          });
          setProvinces(provinceList);
          setProvinceIds(idMap);
        }
      });
    }
  }, [formData.negara, rawCountries]);

  useEffect(() => {
    if (formData.provinsi && provinceIds[formData.provinsi]) {
      setCities([]);
      const provinceId = provinceIds[formData.provinsi];

      fetchWithCache(
        `https://secure.geonames.org/childrenJSON?geonameId=${provinceId}&username=${geoNamesUsername}&lang=id`,
        `cities_${provinceId}`,
        "cities"
      ).then((data) => {
        if (data?.geonames) {
          const cityList = data.geonames.map((city) => ({
            value: city.name,
            label: city.name,
          }));
          setCities(cityList);
        }
      });
    }
  }, [formData.provinsi, provinceIds]);

  const validateField = (name, value) => {
    if (!value && formData[name]?.required) {
      return `${name} wajib diisi`;
    }
    return "";
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };

    if (name === "negara") {
      updatedFormData.provinsi = "";
      updatedFormData.kota = "";
    } else if (name === "provinsi") {
      updatedFormData.kota = "";
    }

    handleChange({ target: { name, value } });

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}
      <InputField
        label="Nama Depan"
        name="namaDepan"
        required
        value={formData.namaDepan || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.namaDepan}
        labelColor={labelColor}
      />
      <InputField
        label="Nama Belakang"
        name="namaBelakang"
        required
        value={formData.namaBelakang || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.namaBelakang}
        labelColor={labelColor}
      />

      <InputField
        label="Email"
        name="email"
        type="email"
        required
        value={formData.email}
        onChange={handleChange}
        disabled={true}
        labelColor={labelColor}
      />

      <InputField
        label="Nomor Telepon/WhatsApp"
        name="telepon"
        required
        value={formData.telepon || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.telepon}
        labelColor={labelColor}
      />

      <InputField
        label="Tanggal Lahir"
        name="tanggalLahir"
        type="date"
        required
        value={formData.tanggalLahir || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.tanggalLahir}
        labelColor={labelColor}
      />

      <InputField
        label="Gender"
        name="gender"
        type="select"
        required
        value={normalizeGender(formData.gender)}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        options={[
          { value: "", label: "Pilih Gender" },
          { value: "Laki-laki", label: "Laki-laki" },
          { value: "Perempuan", label: "Perempuan" },
        ]}
        error={errors.gender}
        labelColor={labelColor}
      />

      {showIdentityNumbers && (
        <InputField
          label="Nomor Identitas (NIK)"
          name="nomorIdentitas"
          value={formData.nomorIdentitas || ""}
          onChange={handleFieldChange}
          disabled={isFormDisabled}
          labelColor={labelColor}
        />
      )}

      {showIdentityNumbers && (
        <InputField
          label="Nomor Passport"
          name="passportNumber"
          value={formData.passportNumber || ""}
          onChange={handleFieldChange}
          disabled={isFormDisabled}
          labelColor={labelColor}
        />
      )}

      <InputField
        label="Negara"
        name="negara"
        type="select"
        required
        value={formData.negara || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || loading.countries}
        options={[{ value: "", label: "Pilih Negara" }, ...countries]}
        error={errors.negara}
        aria-busy={loading.countries}
        labelColor={labelColor}
      />
      <InputField
        label="Provinsi"
        name="provinsi"
        type="select"
        required
        value={formData.provinsi || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.negara || loading.provinces}
        options={[{ value: "", label: "Pilih Provinsi" }, ...provinces]}
        error={errors.provinsi}
        aria-busy={loading.provinces}
        labelColor={labelColor}
      />
      <InputField
        label="Kota"
        name="kota"
        type="select"
        required
        value={formData.kota || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.provinsi || loading.cities}
        options={[{ value: "", label: "Pilih Kota" }, ...cities]}
        error={errors.kota}
        aria-busy={loading.cities}
        labelColor={labelColor}
      />
      <InputField
        label="Alamat Lengkap"
        name="alamat"
        required
        value={formData.alamat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.alamat}
        labelColor={labelColor}
      />
    </div>
  );
};
